import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL
  || (typeof window !== 'undefined' && window.location.hostname === 'localhost'
        ? 'http://localhost:3001/api'
        : 'https://radiance-backend-production.up.railway.app/api');
const STORAGE_KEY = 'radiance_api_key';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      const key = localStorage.getItem(STORAGE_KEY);
      if (!key) {
        if (!cancelled) { setUser(null); setLoading(false); }
        return;
      }
      try {
        const res = await fetch(`${API_URL}/auth/me`, {
          headers: { 'Authorization': `Bearer ${key}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setUser(data.username);
        } else if (res.status === 401) {
          localStorage.removeItem(STORAGE_KEY);
          if (!cancelled) { setApiKey(null); setUser(null); }
        }
      } catch (e) {
        // Network error: keep the key cached but don't claim a user.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [apiKey]);

  const login = async (rawKey) => {
    const key = (rawKey || '').trim();
    if (!key) return { success: false, error: 'Please enter your access key' };
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${key}` }
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem(STORAGE_KEY, key);
        setApiKey(key);
        setUser(data.username);
        return { success: true, username: data.username };
      }
      if (res.status === 401) {
        return { success: false, error: 'Invalid access key' };
      }
      return { success: false, error: 'Login failed. Please try again.' };
    } catch (e) {
      return { success: false, error: 'Could not reach the server. Please try again later.' };
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setApiKey(null);
    setUser(null);
  };

  const getAuthHeaders = () => {
    const key = apiKey || localStorage.getItem(STORAGE_KEY);
    return key ? { 'Authorization': `Bearer ${key}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, loading, apiKey, login, logout, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
